<template>
	<v-dialog v-model="isOpen" :content-class="dialogContentClass" :persistent="isPersistent">
		<v-card flat :class="cardContainerClass">
			<v-card-title>
				<div :class="headerClass">{{ $t(headerText, metadata) }}</div>
			</v-card-title>
			<v-container :class="bodyContainerClass">
				<v-row no-gutters>
					<v-col cols="12">
						<div
							:class="bodyContentClass"
							v-html="bodyHTML"
							@click="onBodyClick"
						></div>
					</v-col>
				</v-row>
				<v-row no-gutters>
					<v-col cols="12">
						<div class="">
							<v-btn
								v-if="showConfirm"
								@click="onConfirmButtonClicked"
								:class="confirmClass"
								dark
							>{{ $t(confirmText, metadata) }}</v-btn>
							<v-btn
								v-if="showAltConfirm"
								@click="onAltConfirmButtonClicked"
								:class="altConfirmClass"
								dark
							>{{ $t(altConfirmText, metadata) }}</v-btn>
							<v-btn
								v-if="showCancel"
								@click="onCancelButtonClicked"
								:class="cancelClass"
								text
							>{{ $t(cancelText, metadata) }}</v-btn>
						</div>
					</v-col>
				</v-row>
			</v-container>
		</v-card>
	</v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import ColorsMixin from "@/components/ColorsMixin";

export default {
	name: "AppConfirmationDialog",
	mixins: [ ColorsMixin, ],
	components: {},
	props: {},
	computed: {
		...mapGetters( "confirmationDialog", [
			"isConfirmationDialogOpen",
			"confirmationDialogOptions",
		] ),
		// dialog-specific settings
		dialogContentClass() {
			return this.confirmationDialogOptions.dialog.contentClass;
		},
		cardContainerClass() {
			return this.confirmationDialogOptions.dialog.cardContainerClass;
		},
		// header settings
		headerClass() {
			return this.confirmationDialogOptions.header.class;
		},
		headerText() {
			return this.confirmationDialogOptions.header.text;
		},
		// body settings
		bodyContainerClass() {
			return this.confirmationDialogOptions.body.containerClass;
		},
		bodyContentClass() {
			return this.confirmationDialogOptions.body.contentClass;
		},
		bodyHTML() {
			const { text, } = this.confirmationDialogOptions.body;
			if ( Array.isArray( text ) ) {
				return text.map( line => this.$t( line, this.metadata ) )
					.map( line => `<p>${ line }</p>` )
					.join( "" )
					.trim();
			}
			return this.$t( text, this.metadata );
		},
		// confirm button settings
		showConfirm() {
			return !!this.confirmationDialogOptions.confirm.buttonText;
		},
		confirmAction() {
			return this.confirmationDialogOptions.confirm.action;
		},
		confirmCallback() {
			return this.confirmationDialogOptions.confirm.callback;
		},
		confirmClass() {
			return this.confirmationDialogOptions.confirm.class;
		},
		confirmText() {
			return this.confirmationDialogOptions.confirm.buttonText;
		},
		closeOnConfirm() {
			return !!this.confirmationDialogOptions.confirm.close;
		},
		// alternate confirm button settings
		showAltConfirm() {
			return !!this.confirmationDialogOptions.altConfirm.buttonText;
		},
		altConfirmAction() {
			return this.confirmationDialogOptions.altConfirm.action;
		},
		altConfirmCallback() {
			return this.confirmationDialogOptions.altConfirm.callback;
		},
		altConfirmClass() {
			return this.confirmationDialogOptions.altConfirm.class;
		},
		altConfirmText() {
			return this.confirmationDialogOptions.altConfirm.buttonText;
		},
		// cancel button settings
		showCancel() {
			return !!this.confirmationDialogOptions.cancel.buttonText;
		},
		cancelAction() {
			return this.confirmationDialogOptions.cancel.action;
		},
		cancelCallback() {
			return this.confirmationDialogOptions.cancel.callback;
		},
		cancelClass() {
			return this.confirmationDialogOptions.cancel.class;
		},
		cancelText() {
			return this.confirmationDialogOptions.cancel.buttonText;
		},
		// other
		isOpen: {
			get() {
				return this.isConfirmationDialogOpen;
			},
			set( isOpen ) {
				this.toggleConfirmationDialog( isOpen );
			},
		},
		metadata() {
			return this.confirmationDialogOptions.metadata;
		},
		isPersistent() {
			return !!this.confirmationDialogOptions?.isPersistent;
		},
	},
	watch: {},
	methods: {
		...mapMutations( "confirmationDialog", [
			"openConfirmationDialog",
			"closeConfirmationDialog",
			"toggleConfirmationDialog",
		] ),
		onCancelButtonClicked() {
			if ( this.cancelAction ) {
				this.$nextTick( () => {
					this.$store.dispatch( this.cancelAction, { ...this.metadata, } );
				} );
			}
			if ( this.cancelCallback ) {
				this.$nextTick( () => {
					this.cancelCallback();
				} );
			}
			this.closeConfirmationDialog();
		},
		onConfirmButtonClicked() {
			if ( this.confirmAction ) {
				this.$nextTick( () => {
					this.$store.dispatch( this.confirmAction, { ...this.metadata, } );
				} );
			}
			if ( this.confirmCallback ) {
				this.$nextTick( () => {
					this.confirmCallback();
				} );
			}
			if ( this.closeOnConfirm ) {
				this.closeConfirmationDialog();
			}
		},
		onAltConfirmButtonClicked() {
			if ( this.altConfirmAction ) {
				this.$nextTick( () => {
					this.$store.dispatch( this.altConfirmAction, { ...this.metadata, } );
				} );
			}
			if ( this.altConfirmCallback ) {
				this.$nextTick( () => {
					this.altConfirmCallback();
				} );
			}
			this.closeConfirmationDialog();
		},
		onBodyClick( e ) {
			e.preventDefault();
			e.stopPropagation();
			const action = e.target.getAttribute( "data-action" );
			if ( !action ) {
				return;
			}
			this.$nextTick( () => {
				// schedule|chat
				this.$store.dispatch( action, { ...this.metadata, } );
			} );
		},
	},
};
</script>

<style lang="scss">
.app-confirmation-dialog {
	max-width: 500px !important;
}
</style>

<style lang="scss" scoped>
.app-confirmation-dialog__container {
	border-radius: 8px !important;
}

.app-confirmation-dialog__header {
	height: 20px;
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 20px;
	color: $gray-700;
}

.app-confirmation-dialog__body-container {
	padding-top: 0px !important;
	padding-bottom: 15px !important;
}

.app-confirmation-dialog__body-content {
	font-style: normal;
	font-weight: 500;
	font-size: 11.667px;
	line-height: 16px;
	color: $gray-500;
	padding-bottom: 15px;
}

.app-confirmation-dialog__confirm-button{
	width: 100px !important;
	border-radius: 8px;
	text-transform: none !important;
	margin-left: 0px !important;
	background: $blue-500 !important;
}

.app-confirmation-dialog__alt-confirm-button {
	width: 100px !important;
	border-radius: 8px;
	text-transform: none !important;
	margin-left: 0px !important;
	background: $blue-500 !important;
}

.app-confirmation-dialog__cancel-button {
	width: 100px !important;
	border-radius: 8px;
	text-transform: none !important;
	margin-left: 0px !important;
	background: $gray-050;
	color: $gray-500 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/AppConfirmationDialog.vue